import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useSeo, SEO_KEYWORDS } from '../hooks/useSeo';

const ResetPasswordPage = () => {
  useSeo({ title: 'Choose a New Password | InvestiGate', description: 'Set a new password for your InvestiGate account.', keywords: SEO_KEYWORDS, noindex: true });
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState(token ? 'idle' : 'invalid'); // idle | loading | success | error | invalid
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setState('loading');
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`,
        { token, new_password: password },
        { withCredentials: true }
      );
      setState('success');
    } catch (err) {
      setState('error');
      setError(err.response?.data?.detail || 'This reset link is invalid or expired.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Shield className="w-12 h-12 text-blue-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          {state === 'success' ? 'Password updated' : state === 'invalid' ? 'Invalid reset link' : 'Choose a new password'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {state === 'success' ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-sm text-gray-700">Your password has been reset successfully.</p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                data-testid="reset-goto-login"
              >
                Go to login
              </Link>
            </div>
          ) : state === 'invalid' ? (
            <div className="text-center space-y-4" data-testid="reset-invalid">
              <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-600" />
              </div>
              <p className="text-sm text-gray-700">
                This reset link is missing or invalid. Request a new one below.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center justify-center w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                data-testid="reset-request-new"
              >
                Request new link
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit} noValidate>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  data-testid="reset-password-input"
                />
                <p className="mt-1 text-xs text-gray-500">At least 8 characters.</p>
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-gray-700">
                  Confirm password
                </label>
                <input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  data-testid="reset-confirm-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" role="alert" data-testid="reset-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={state === 'loading' || !password || !confirm}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {state === 'loading' ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
